import styled from '@emotion/styled';
import Box from '@mui/material/Box';
import Tooltip from '@mui/material/Tooltip';
import { DistributiveOmit } from '@mui/types';
import * as Sentry from '@sentry/react';
import { useQueryClient } from '@tanstack/react-query';
import {
  deleteV1CompaniesCompanyIdOrdersOrderIdDefaultSitesWaypointTypeSiteId,
  deleteV1CompaniesCompanyIdProjectsProjectIdDefaultSitesWaypointTypeSiteId,
  DeleteV1CompaniesCompanyIdProjectsProjectIdDefaultSitesWaypointTypeSiteIdData,
  ModelError_Item,
  ModelError_Response,
  Site_Read_Nested,
  SiteType,
  WaypointType,
} from '@treadinc/horizon-api-spec';
import circle from '@turf/circle';
import { polygon } from '@turf/helpers';
import { AxiosError, AxiosResponse, isAxiosError } from 'axios';
import dayjs from 'dayjs';
import type { Polygon } from 'geojson';
import { t } from 'i18next';
import { ComponentProps, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';

import Dialog from '~components/Dialog/Dialog';
import DialogActions from '~components/Dialog/DialogActions';
import DialogContent from '~components/Dialog/DialogContent';
import DialogHeader from '~components/Dialog/DialogHeader';
import { FormFieldLabel } from '~components/FormFields/FormFieldLabel';
import { PseudoLink } from '~components/Helpers/PseudoLink';
import { LngLat, MarkerProps } from '~components/Maps/interfaces';
import { GeofenceType, MarkerType } from '~components/Maps/v2/constants';
import { MapV2 } from '~components/Maps/v2/MapV2';
import { TreadMarkerProps } from '~components/Maps/v2/types';
import { AddressType } from '~constants/enums';
import { FeatureFlags } from '~constants/featureFlags';
import { AddressItem } from '~hooks/useAddress/models/AddressItem';
import { CompanyBasic } from '~hooks/useCompany/models';
import { useCompany } from '~hooks/useCompany/useCompany';
import { Project } from '~hooks/useProjects/models';
import { Geofence, Site, SiteBasic } from '~hooks/useSites/models';
import { type DefaultSitesWaypointType, useSites } from '~hooks/useSites/useSites';
import { SiteWithAdditionalGeoFences } from '~pages/Settings/Administration/Sites/GeoFenceForm';
import { useSiteFormV2 } from '~pages/Settings/Administration/Sites/site-form-v2/hooks';
import {
  SiteFormV2,
  type SiteFormV2Handler,
} from '~pages/Settings/Administration/Sites/site-form-v2/SiteFormV2';
import {
  type SiteFormV2DirtyFields,
  SiteFormV2Fields,
  type SiteFormV2StateChangeProps,
} from '~pages/Settings/Administration/Sites/site-form-v2/types';
import useDefaultSitesQuery from '~queries/useDefaultSitesQuery';
import { useStores } from '~store';
import { alert, AlertTypes } from '~types/AlertTypes';
import { Nullable } from '~types/Nullable';
import { useFeatureFlag } from '~utils/hooks/useFeatureFlag';
import { formatAddressOptionForSiteForm, getFormattedLatLng } from '~utils/sites';
import { getEffectiveUserCompanyId } from '~utils/user/user-utils';

import { SmallButton, SmallLoadingButton } from '../ordersDispatchStyledComponents';
import {
  SiteSelectorWithDefaultSites,
  SiteSelectorWithDefaultSitesHandler,
} from './SiteSelectorWithDefaultSites';
import { AddressOption, AddressOptionForNewSite } from './types';
import { Form } from './types';
import { AddressType as AddressTypeSection } from './types';

type DefaultSiteMarker = DistributiveOmit<
  TreadMarkerProps,
  'onMouseDown' | 'onMouseOut' | 'onMouseOver' | 'onMouseUp'
>;

interface SiteLocationFormProps {
  companyId?: Nullable<string>;
  ignoreCompanyNotFound?: boolean;
  orderId?: Nullable<string>;
  pickUpSite?: Nullable<SiteBasic>;
  dropOffSite?: Nullable<SiteBasic>;
  stagingSite?: Nullable<SiteBasic>;
  onPickUpSiteChange: (value?: Site) => void;
  onDropOffSiteChange: (value?: Site) => void;
  onStagingSiteChange?: (value?: Site) => void;
  errors?: {
    pickUpWayPoint?: object;
    dropOffWayPoint?: object;
    stagingWayPoint?: object;
  };
  allowSuggestions?: boolean;
  isRequired?: boolean;
  projectId?: Nullable<string>;
  project?: Nullable<Project>;
  onlyDefaultSites?: boolean;
  prefilledPickUpValue?: string;
  prefilledDropOffValue?: string;
  prefilledStagingValue?: string;
  onDefaultSiteAdded?: ({
    waypointType,
    siteId,
  }: {
    waypointType: DefaultSitesWaypointType;
    siteId: string;
  }) => void;
  // Counterpart of `onDefaultSiteAdded` for New Order / New Project, where an
  // added default site is only an optimistic local entry: the parent form must
  // drop the matching pending post-create POST when the user undoes it.
  onTempDefaultSiteRemoved?: ({
    waypointType,
    siteId,
  }: {
    waypointType: DefaultSitesWaypointType;
    siteId: string;
  }) => void;
  hideAddDefaultSiteAction?: boolean;
  allowRemoveDefaultSites?: boolean;
  showMap?: boolean;
  onlyShowDefaultSiteMarkers?: boolean;
  disablePickUpEditCta?: boolean;
  disableDropOffEditCta?: boolean;
  disableStagingEditCta?: boolean;
  disableSiteSelection?: boolean;
}

interface SiteSelectionLabelProps {
  label: string;
  isRequired: boolean;
  editCta: {
    disabled?: boolean;
    disabledReason?: 'site-discarded';
    label: string;
    onClick: () => void;
  };
}

const checkAndAddDroppedPin = (str: string): string => {
  const droppedPin = '(Dropped pin)';
  if (!str.includes(droppedPin)) {
    return `${droppedPin} ${str}`;
  }
  return str;
};

const LabelWrapper = styled(Box)`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const RequiredLabel = styled('sup')`
  top: 0;
`;

const SiteSelectionLabel = ({ label, editCta, isRequired }: SiteSelectionLabelProps) => {
  let link = (
    <PseudoLink
      disabled={editCta.disabled}
      title={editCta.label}
      action={editCta.onClick}
      sx={{ lineHeight: '10px' }}
    />
  );

  if (editCta.disabled && editCta.disabledReason === 'site-discarded') {
    link = (
      <Tooltip title={t('administration.site.deleted_sites_cannot_be_edited')}>
        <span>{link}</span>
      </Tooltip>
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
      }}
    >
      {isRequired ? (
        <LabelWrapper>
          <FormFieldLabel label={label} />
          <RequiredLabel>*</RequiredLabel>
        </LabelWrapper>
      ) : (
        <FormFieldLabel label={label} />
      )}
      {link}
    </Box>
  );
};

interface SelectedSite {
  site: Nullable<Site>;
  type: WaypointType;
}

interface SelectedAddressOption {
  addressOption: Nullable<AddressOptionForNewSite>;
  type: AddressType;
}

type AddressOptionFormValue = Omit<AddressOption, 'type'>;

// The parent waypoint emit can be killed mid-flight when React aborts a
// nested-update burst with "Maximum update depth exceeded" (HORIZON-FE-2TC,
// observed alongside TRE-16178's silent site loss). Retrying through
// setTimeout escapes that flush — React resets its nested-update counter
// between flushes — so a single retry usually lands; three attempts bound the
// recovery at a non-perceivable ~300ms worst case before failing loudly.
const MAX_APPLY_ADDRESS_ATTEMPTS = 3;
const APPLY_ADDRESS_RETRY_DELAY_MS = 100;

const transformSiteToAddressOption = (site: SiteBasic | Site): AddressOptionFormValue => {
  return {
    name: site.name,
    lat: site.lat || '',
    lng: site.lon || '',
    id: site.id,
    siteId: site.id,
    externalId: (site as Site).externalId || '',
    geofence: site.nextBillionGeofence,
    discardedAt: site.discardedAt ? dayjs.tz(site.discardedAt) : null,
  };
};

const buildMovingSiteMarker = ({
  geofence,
  id,
  lat,
  lng,
}: {
  geofence: Geofence;
  id: string;
  lat: number;
  lng: number;
}): DefaultSiteMarker => ({
  id,
  lat,
  lng,
  type: MarkerType.moving_site,
  radii: [geofence.circleRadius],
  circleCenter: {
    lat: geofence.circleCenter?.lat ?? lat,
    lng: geofence.circleCenter?.lon ?? lng,
  },
});

export const SiteSelection = ({
  companyId,
  ignoreCompanyNotFound,
  orderId,
  pickUpSite,
  dropOffSite,
  stagingSite,
  onPickUpSiteChange,
  onDropOffSiteChange,
  onStagingSiteChange,
  errors: errorsProp,
  isRequired,
  allowSuggestions,
  projectId,
  project,
  onDefaultSiteAdded,
  onTempDefaultSiteRemoved,
  hideAddDefaultSiteAction,
  allowRemoveDefaultSites,
  onlyDefaultSites = false,
  showMap = true,
  prefilledPickUpValue,
  prefilledDropOffValue,
  prefilledStagingValue,
  onlyShowDefaultSiteMarkers,
  disablePickUpEditCta,
  disableDropOffEditCta,
  disableStagingEditCta,
  disableSiteSelection,
}: SiteLocationFormProps) => {
  const { toasterStore, userStore } = useStores();
  const selectedCompanyId = getEffectiveUserCompanyId(userStore);
  const formCompanyId = companyId || selectedCompanyId;
  const queryClient = useQueryClient();
  const { getCompanyById, getUserCompanyDefaultMapCenter } = useCompany();
  const { getSiteById } = useSites();
  const { createNewSite: createNewSiteV2, updateSite: updateSiteV2 } = useSiteFormV2();
  const isStagingWaypointEnabled = useFeatureFlag({
    featureFlagKey: FeatureFlags.stagingWaypoint,
  });

  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isCompanyLoading, setIsCompanyLoading] = useState(false);
  const [isLoadingSiteData, setIsLoadingSiteData] = useState(false);

  const [company, setCompany] = useState<Nullable<CompanyBasic>>(null);

  const [selectedAddressOption, setSelectedAddressOption] =
    useState<Nullable<SelectedAddressOption>>(null);
  const [selectedSiteToEdit, setSelectedSiteToEdit] =
    useState<Nullable<SelectedSite>>(null);
  const [selectedPickUpSite, setSelectedPickUpSite] =
    useState<Nullable<SiteBasic>>(pickUpSite);
  const [selectedDropOffSite, setSelectedDropOffSite] =
    useState<Nullable<SiteBasic>>(dropOffSite);
  const [selectedStagingSite, setSelectedStagingSite] =
    useState<Nullable<SiteBasic>>(stagingSite);
  const [markers, setMarkers] = useState<MarkerProps[]>([]);
  const [asyncErrors, setAsyncErrors] = useState<ModelError_Item[]>([]);

  const siteFormRef = useRef<SiteFormV2Handler>(null);

  const defaultPosition = getUserCompanyDefaultMapCenter();
  const isDialogBusy = isSaving || isCompanyLoading || isLoadingSiteData;

  const {
    control,
    watch,
    setValue,
    getValues,
    formState: { errors },
    setError,
    clearErrors,
  } = useForm({
    mode: 'onSubmit',
    defaultValues: {
      pickUpAddress: pickUpSite
        ? transformSiteToAddressOption(pickUpSite)
        : (null as Nullable<AddressOptionFormValue>),
      dropOffAddress: dropOffSite
        ? transformSiteToAddressOption(dropOffSite)
        : (null as Nullable<AddressOptionFormValue>),
      stagingAddress: stagingSite
        ? transformSiteToAddressOption(stagingSite)
        : (null as Nullable<AddressOptionFormValue>),
      pickUpLatLng: '',
      dropOffLatLng: '',
      stagingLatLng: '',
    },
  });

  const handleCloseDialog = useCallback(() => {
    setIsDialogOpen(false);
  }, []);

  const previousSeededProjectIdRef = useRef<Nullable<string>>(null);

  useEffect(() => {
    if (orderId || !project) {
      return;
    }

    const projectChanged = previousSeededProjectIdRef.current !== project.id;
    previousSeededProjectIdRef.current = project.id;

    const seedAddressFromProjectSite = (
      name: 'pickUpAddress' | 'dropOffAddress' | 'stagingAddress',
      latLngName: 'pickUpLatLng' | 'dropOffLatLng' | 'stagingLatLng',
      changeFunc: (site: Site) => void,
      aType: AddressType,
      projectSite: Nullable<SiteBasic> | undefined,
    ) => {
      const currentAddress = getValues(name);

      if (projectSite) {
        if (projectChanged || !currentAddress) {
          setValue(name, transformSiteToAddressOption(projectSite));
          applyAddressChange(name, latLngName, changeFunc, aType);
        }
      } else if (projectChanged && currentAddress) {
        setValue(name, null);
        applyAddressChange(name, latLngName, changeFunc, aType);
      }
    };

    seedAddressFromProjectSite(
      'pickUpAddress',
      'pickUpLatLng',
      onPickUpSiteChange,
      AddressType.PickUp,
      project.pickUpWayPoints?.[0]?.site,
    );
    seedAddressFromProjectSite(
      'dropOffAddress',
      'dropOffLatLng',
      onDropOffSiteChange,
      AddressType.DropOff,
      project.dropOffWayPoints?.[0]?.site,
    );
    if (isStagingWaypointEnabled) {
      seedAddressFromProjectSite(
        'stagingAddress',
        'stagingLatLng',
        onStagingSiteChange ?? (() => {}),
        AddressType.Staging,
        project.stagingWayPoints?.[0]?.site,
      );
    }
  }, [orderId, project, isStagingWaypointEnabled]);

  // NOTE: the per-waypoint memos below spread this object but intentionally
  // keep their existing narrow dependency arrays, so the callbacks captured
  // here must be identity-stable in every parent form.
  const siteSelectorProps = useMemo(
    () => ({
      onDefaultSiteAdded,
      onTempDefaultSiteRemoved,
      control,
      latLngLabel: t('form_fields.coordinates'),
      isRequired,
      allowSuggestions,
    }),
    [
      allowSuggestions,
      onDefaultSiteAdded,
      onTempDefaultSiteRemoved,
      control,
      isRequired,
      selectedPickUpSite,
    ],
  );
  const pickUpSelectorProps = useMemo(
    () => ({
      ...siteSelectorProps,
      waypointType: WaypointType.PICKUP as DefaultSitesWaypointType,
      addressName: 'pickUpAddress',
      latLngName: 'pickUpLatLng',
      addressLabel: '',
      renderCustomLabel: ({ isRequired = false }) => (
        <SiteSelectionLabel
          label={t('form_fields.pick_up_site')}
          isRequired={isRequired}
          editCta={{
            disabled: disablePickUpEditCta
              ? true
              : selectedPickUpSite?.id
                ? Boolean(selectedPickUpSite?.discardedAt)
                : false,
            disabledReason: selectedPickUpSite?.discardedAt
              ? 'site-discarded'
              : undefined,
            label: selectedPickUpSite
              ? t('administration.site.edit_selected_pick_up_site')
              : t('administration.site.create_new_site'),
            onClick: () =>
              onCreateEditSiteClick({
                companyId: companyId ?? '',
                siteId: selectedPickUpSite?.id,
                addressType: WaypointType.PICKUP,
              }),
          }}
        />
      ),
    }),
    [selectedPickUpSite, isRequired, disablePickUpEditCta],
  );

  const dropOffSelectorProps = useMemo(
    () => ({
      ...siteSelectorProps,
      waypointType: WaypointType.DROP_OFF as DefaultSitesWaypointType,
      addressName: 'dropOffAddress',
      latLngName: 'dropOffLatLng',
      addressLabel: '',
      renderCustomLabel: ({ isRequired = false }) => (
        <SiteSelectionLabel
          label={t('form_fields.drop_off_site')}
          isRequired={isRequired}
          editCta={{
            disabled: disableDropOffEditCta
              ? true
              : selectedDropOffSite?.id
                ? Boolean(selectedDropOffSite?.discardedAt)
                : false,
            disabledReason: selectedDropOffSite?.discardedAt
              ? 'site-discarded'
              : undefined,
            label: selectedDropOffSite
              ? t('administration.site.edit_selected_drop_off_site')
              : t('administration.site.create_new_site'),
            onClick: () =>
              onCreateEditSiteClick({
                companyId: companyId ?? '',
                siteId: selectedDropOffSite?.id,
                addressType: WaypointType.DROP_OFF,
              }),
          }}
        />
      ),
    }),
    [selectedDropOffSite, isRequired, disableDropOffEditCta],
  );

  const stagingSelectorProps = useMemo(
    () => ({
      ...siteSelectorProps,
      addressName: 'stagingAddress',
      latLngName: 'stagingLatLng',
      addressLabel: '',
      isRequired: false,
      renderCustomLabel: () => (
        <SiteSelectionLabel
          label={t('form_fields.staging_site')}
          isRequired={false}
          editCta={{
            disabled: disableStagingEditCta
              ? true
              : selectedStagingSite?.id
                ? Boolean(selectedStagingSite?.discardedAt)
                : false,
            disabledReason: selectedStagingSite?.discardedAt
              ? 'site-discarded'
              : undefined,
            label: selectedStagingSite
              ? t('administration.site.edit_selected_staging_site')
              : t('administration.site.create_new_site'),
            onClick: () =>
              onCreateEditSiteClick({
                companyId: companyId ?? '',
                siteId: selectedStagingSite?.id,
                addressType: WaypointType.STAGING,
              }),
          }}
        />
      ),
    }),
    [selectedStagingSite, disableStagingEditCta],
  );

  const watchPickUpAddress = watch('pickUpAddress');
  const watchDropOffAddress = watch('dropOffAddress');
  const watchStagingAddress = watch('stagingAddress');

  const applyAddressRetryTimeouts = useRef<Partial<Record<keyof Form, number>>>({});
  const addressesWithFailedApply = useRef<Set<keyof Form>>(new Set());
  const lastAppliedAddressesRef = useRef<
    Partial<Record<keyof Form, Nullable<AddressOptionFormValue>>>
  >({});

  const applyAddressChange = (
    name: keyof Form,
    latLngName: keyof Form,
    changeFunc: (site: Site) => void,
    aType: AddressType,
    attempt = 1,
  ) => {
    window.clearTimeout(applyAddressRetryTimeouts.current[name]);

    try {
      handleAddressChange(name, latLngName, changeFunc, aType);
      lastAppliedAddressesRef.current[name] =
        (getValues(name) as Nullable<AddressOptionFormValue>) ?? null;

      if (addressesWithFailedApply.current.has(name)) {
        addressesWithFailedApply.current.delete(name);
        clearErrors(name);
      }
    } catch (error) {
      if (attempt < MAX_APPLY_ADDRESS_ATTEMPTS) {
        applyAddressRetryTimeouts.current[name] = window.setTimeout(() => {
          applyAddressChange(name, latLngName, changeFunc, aType, attempt + 1);
        }, APPLY_ADDRESS_RETRY_DELAY_MS);

        return;
      }

      Sentry.captureException(error);
      addressesWithFailedApply.current.add(name);
      setError(name, {
        message: t('error_messages.sites.failed_to_apply_site_selection'),
      });
    }
  };

  useEffect(() => {
    const retryTimeouts = applyAddressRetryTimeouts.current;

    return () => {
      Object.values(retryTimeouts).forEach((timeoutId) => {
        window.clearTimeout(timeoutId);
      });
    };
  }, []);

  useEffect(() => {
    const addressIsAlreadyApplied =
      (watchPickUpAddress ?? null) ===
      (lastAppliedAddressesRef.current.pickUpAddress ?? null);
    if (addressIsAlreadyApplied) {
      return;
    }

    applyAddressChange(
      'pickUpAddress',
      'pickUpLatLng',
      onPickUpSiteChange,
      AddressType.PickUp,
    );
  }, [watchPickUpAddress]);

  useEffect(() => {
    const addressIsAlreadyApplied =
      (watchDropOffAddress ?? null) ===
      (lastAppliedAddressesRef.current.dropOffAddress ?? null);
    if (addressIsAlreadyApplied) {
      return;
    }

    applyAddressChange(
      'dropOffAddress',
      'dropOffLatLng',
      onDropOffSiteChange,
      AddressType.DropOff,
    );
  }, [watchDropOffAddress]);

  useEffect(() => {
    if (isStagingWaypointEnabled) {
      const addressIsAlreadyApplied =
        (watchStagingAddress ?? null) ===
        (lastAppliedAddressesRef.current.stagingAddress ?? null);
      if (addressIsAlreadyApplied) {
        return;
      }

      applyAddressChange(
        'stagingAddress',
        'stagingLatLng',
        onStagingSiteChange ?? (() => {}),
        AddressType.Staging,
      );
    }
  }, [watchStagingAddress, isStagingWaypointEnabled]);

  useEffect(() => {
    const fetchCompany = async () => {
      if (companyId) {
        setIsCompanyLoading(true);
        try {
          const company = await getCompanyById({ id: companyId, ignoreCompanyNotFound });
          setCompany(company ? (company as unknown as CompanyBasic) : null);
        } finally {
          setIsCompanyLoading(false);
        }
      }
    };
    fetchCompany();
  }, [companyId, ignoreCompanyNotFound]);

  useEffect(() => {
    if (errorsProp?.pickUpWayPoint) {
      setError('pickUpAddress', {
        message:
          'message' in errorsProp.pickUpWayPoint
            ? (errorsProp.pickUpWayPoint.message as string)
            : t('form_validation_errors.required', {
                field: t('form_fields.pick_up_site'),
              }),
      });
    }

    if (errorsProp?.dropOffWayPoint) {
      setError('dropOffAddress', {
        message:
          'message' in errorsProp.dropOffWayPoint
            ? (errorsProp.dropOffWayPoint.message as string)
            : t('form_validation_errors.required', {
                field: t('form_fields.drop_off_site'),
              }),
      });
    }

    if (errorsProp?.stagingWayPoint) {
      setError('stagingAddress', {
        message:
          'message' in errorsProp.stagingWayPoint
            ? (errorsProp.stagingWayPoint.message as string)
            : t('form_validation_errors.required', {
                field: t('form_fields.staging_site'),
              }),
      });
    }

    return () => {
      clearErrors(['pickUpAddress', 'dropOffAddress', 'stagingAddress']);
    };
  }, [
    errorsProp?.pickUpWayPoint,
    errorsProp?.dropOffWayPoint,
    errorsProp?.stagingWayPoint,
  ]);

  const mapCenter = useMemo(() => {
    const lastAddition = markers[markers.length - 1];
    if (lastAddition?.lng && lastAddition?.lat) {
      return {
        lng: lastAddition.lng,
        lat: lastAddition.lat,
      };
    } else {
      return {
        lng: defaultPosition.lng,
        lat: defaultPosition.lat,
      };
    }
  }, [markers, defaultPosition]);
  const [defaultSiteGeoFence, setDefaultSiteGeoFence] = useState<
    {
      geometry: Polygon;
      id?: string | number | undefined;
      properties: object | null;
      type: string;
      siteType: string;
    }[]
  >([]);

  // Must keep Save disabled while SiteFormV2 is still fetching the site's existing
  // additional/moving geofences: submitting before that resolves sends an empty
  // additionalGeofences list, and the backend's bulk_assign discards every existing
  // additional geofence and only recreates what's in that (empty) list.
  const onSiteFormStateChange = ({ isLoadingSiteData }: SiteFormV2StateChangeProps) => {
    setIsLoadingSiteData(isLoadingSiteData);
  };

  const onEditOrderSiteForm = () => {
    siteFormRef.current?.submit(
      async (
        data: SiteWithAdditionalGeoFences | SiteFormV2Fields,
        dirtyFields: SiteFormV2DirtyFields,
      ) => {
        try {
          setIsSaving(true);
          await onSubmitEditOrderSiteFormCallBack(data, dirtyFields);
        } finally {
          setIsSaving(false);
        }
      },
    );
  };

  const onCreateNewSiteForm = () => {
    siteFormRef.current?.submit(
      async (data: SiteWithAdditionalGeoFences | SiteFormV2Fields) => {
        try {
          setIsSaving(true);
          await onSubmitNewSiteFormCallBack(data);
        } finally {
          setIsSaving(false);
        }
      },
    );
  };

  const onSubmitEditOrderSiteFormCallBack = async (
    data: SiteWithAdditionalGeoFences | SiteFormV2Fields,
    dirtyFields: SiteFormV2DirtyFields,
  ) => {
    if (!selectedSiteToEdit?.site) {
      return;
    }

    await updateSiteV2(
      { id: selectedSiteToEdit.site.id, ...(data as SiteFormV2Fields) },
      dirtyFields,
    )
      .then((site: Site | undefined) => {
        if (site) {
          updateFormSite(site);
        }

        handleCloseDialog();
      })
      .catch((error: AxiosError<ModelError_Response>) => {
        setAsyncErrors(error.response?.data.error.errors || []);
      });
  };

  const onSubmitNewSiteFormCallBack = async (
    data: SiteWithAdditionalGeoFences | SiteFormV2Fields,
  ) => {
    await createNewSiteV2({ ...data } as SiteFormV2Fields)
      .then((site: Site | undefined) => {
        if (site) {
          updateFormSite(site);
        }

        handleCloseDialog();
      })
      .catch((error) => {
        setAsyncErrors(error.response.data.error.errors);
      });
  };

  const onCreateEditSiteClick = async ({
    companyId,
    siteId,
    addressType,
  }: {
    companyId: string;
    siteId?: string;
    addressType: WaypointType;
  }) => {
    const siteData =
      (siteId && companyId && (await getSiteById(companyId, siteId))) || null;

    setSelectedSiteToEdit({ site: siteData, type: addressType });
    setIsDialogOpen(true);
  };

  const updateSiteOnAddressChange = ({
    name,
    selectedSite,
  }: {
    name: keyof Form;
    selectedSite: Site;
  }) => {
    setSelectedAddressOption(null);

    if (name === 'pickUpAddress') {
      setSelectedPickUpSite(selectedSite as unknown as SiteBasic);
    } else if (name === 'dropOffAddress') {
      setSelectedDropOffSite(selectedSite as unknown as SiteBasic);
    } else if (name === 'stagingAddress') {
      setSelectedStagingSite(selectedSite as unknown as SiteBasic);
    }
  };

  const updateAddressOptionOnSiteChange = ({
    addressOption,
    waypointType,
  }: {
    addressOption: AddressOption;
    waypointType: AddressType;
  }) => {
    const formattedAddressOption = formatAddressOptionForSiteForm({
      addressOption,
    });
    if (waypointType === AddressType.PickUp) {
      setSelectedPickUpSite(null);
    } else if (waypointType === AddressType.DropOff) {
      setSelectedDropOffSite(null);
    } else if (waypointType === AddressType.Staging) {
      setSelectedStagingSite(null);
    }

    setSelectedAddressOption({
      type: waypointType,
      addressOption: formattedAddressOption,
    });
  };

  const handleAddressChange = (
    name: keyof Form,
    latLngName: keyof Form,
    changeFunc: (site: Site) => void,
    aType: AddressType,
  ) => {
    const address = getValues(name) as Nullable<AddressOption>;

    if (!address) {
      handleNoAddressChange(name, aType, latLngName);
      return;
    }

    if (address?.geofence?.geojson) {
      const polygonFromFenceCoords = polygon(
        address?.geofence?.geojson?.coordinates,
        {},
        // ID will upsert the polygon on the map
        { id: address?.geofence?.id },
      );

      setDefaultSiteGeoFence((prev) => {
        const filteredFences = prev.filter((fence) => fence.siteType !== name);
        return [...filteredFences, { ...polygonFromFenceCoords, siteType: name }];
      });
    } else if (address.geofence?.circleCenter && address.geofence?.circleRadius) {
      const center = [
        address.geofence.circleCenter.lon,
        address.geofence.circleCenter.lat,
      ];

      const circleFence = circle(center, address.geofence.circleRadius, {
        units: 'meters',
      });

      setDefaultSiteGeoFence((prev) => {
        const filteredFences = prev.filter((fence) => fence.siteType !== name);
        return [
          ...filteredFences,
          {
            ...circleFence,
            siteType: name,
          },
        ];
      });
    }

    const { lat, lng } = getFormattedLatLng(address.lat as string, address.lng as string);

    // If the change is to a suggestion,
    // Update the selected address option and null
    // Out the selected site for the address type

    const site: Site_Read_Nested = {
      id: address.siteId || '',
      name: address.name,
      lat: lat,
      lon: lng,
      full_address: '',
      routable: !!(lat && lng),
      discarded_at: address.discardedAt?.toISOString() ?? null,
      external_id: null,
      site_type: SiteType.OTHER,
      open_at: null,
      close_at: null,
      time_zone: null,
      haul: true,
      equipment_types: [],
    };

    if (address.address) {
      site.address = AddressItem.deparse(address.address);
    }

    const selectedSite = Site.parseNested(site);

    setValue(latLngName, `${lat}, ${lng}`);
    updateMarkers(aType, lat, lng, name, address);

    changeFunc(selectedSite);

    if (address.type === AddressTypeSection.Suggestions) {
      updateAddressOptionOnSiteChange({
        addressOption: address,
        waypointType: aType,
      });
    } else {
      updateSiteOnAddressChange({ name, selectedSite });
    }
  };

  const updateMarkers = (
    aType: AddressType,
    lat: string,
    lng: string,
    name: keyof Form,
    address: AddressOption,
  ) => {
    const markerId = String(aType);

    setMarkers((prev) => {
      const filteredMarkers = prev.filter((marker) => marker.id !== markerId);
      const newMarker: MarkerProps = {
        id: markerId,
        lng: Number.parseFloat(lng),
        lat: Number.parseFloat(lat),
        draggable: !address.siteId,
        onDragMarker: (e: LngLat) => {
          if (!address.siteId) {
            setValue(name, {
              ...address,
              lat: e.lat.toFixed(6),
              lng: e.lng.toFixed(6),
              name: checkAndAddDroppedPin(address.name),
            });
          }
        },
      };

      return [...filteredMarkers, newMarker];
    });
  };

  const handleNoAddressChange = (
    name: keyof Form,
    aType: AddressType,
    latLngName: keyof Form,
  ) => {
    setDefaultSiteGeoFence((prev) => {
      return prev.filter((fence) => fence.siteType !== name);
    });
    setMarkers((prev) => prev.filter((marker) => marker.id !== String(aType)));
    setValue(latLngName, '');

    if (name === 'pickUpAddress') {
      onPickUpSiteChange(undefined);
      setSelectedPickUpSite(null);
    } else if (name === 'dropOffAddress') {
      onDropOffSiteChange(undefined);
      setSelectedDropOffSite(null);
    } else if (name === 'stagingAddress') {
      onStagingSiteChange?.(undefined);
      setSelectedStagingSite(null);
    }
  };

  const updateFormSite = (site: Site) => {
    const addressType = selectedSiteToEdit?.type;

    // Update the order/project form site value with the newly
    // Create site
    if (addressType) {
      if (addressType === WaypointType.PICKUP) {
        onPickUpSiteChange(site);
      } else if (addressType === WaypointType.DROP_OFF) {
        onDropOffSiteChange(site);
      } else if (addressType === WaypointType.STAGING) {
        onStagingSiteChange?.(site);
      }

      // Update the selected site to edit with the newly created site
      setSelectedSiteToEdit({ site, type: addressType });

      if (addressType === WaypointType.PICKUP) {
        setValue('pickUpAddress', transformSiteToAddressOption(site));
      } else if (addressType === WaypointType.DROP_OFF) {
        setValue('dropOffAddress', transformSiteToAddressOption(site));
      } else if (addressType === WaypointType.STAGING) {
        setValue('stagingAddress', transformSiteToAddressOption(site));
      }
    }
  };

  const markersV2 = useMemo(() => {
    const dropOffAddress = getValues('dropOffAddress');
    const pickUpAddress = getValues('pickUpAddress');
    const stagingAddress = getValues('stagingAddress');

    const markerEntries: [string, string, MarkerType][] = [
      [
        getValues('dropOffLatLng'),
        'dropOffAddress',
        dropOffAddress?.geofence?.movingGeofence
          ? MarkerType.moving_site
          : MarkerType.dropoff_site,
      ],
      [
        getValues('pickUpLatLng'),
        'pickUpAddress',
        pickUpAddress?.geofence?.movingGeofence
          ? MarkerType.moving_site
          : MarkerType.pickup_site,
      ],
    ];

    if (isStagingWaypointEnabled && stagingAddress) {
      markerEntries.push([
        getValues('stagingLatLng'),
        'stagingAddress',
        stagingAddress?.geofence?.movingGeofence
          ? MarkerType.moving_site
          : MarkerType.staging_site,
      ]);
    }

    return (
      markerEntries
        // A site with no coordinates serializes to ", " — non-empty but parses to
        // NaN. A NaN marker reaches Google Maps and crashes the deck.gl overlay
        // (Sentry HORIZON-FE-2XP), so keep only entries with finite coordinates.
        .filter(([latLngStr]) => {
          const [lat, lng] = latLngStr
            .split(',')
            .map((value) => Number.parseFloat(value));
          return Number.isFinite(lat) && Number.isFinite(lng);
        })
        .map(([latLngStr, siteType, markerType], ind) => {
          const commonProps = {
            id: `${latLngStr} ind-${ind}`,
            type: markerType,
            lat: Number.parseFloat(latLngStr.split(',')[0]),
            lng: Number.parseFloat(latLngStr.split(',')[1]),
          };

          const relevantGeofence = defaultSiteGeoFence.find(
            (fence) => fence.siteType === siteType,
          );

          const address =
            siteType === 'dropOffAddress'
              ? dropOffAddress
              : siteType === 'stagingAddress'
                ? stagingAddress
                : pickUpAddress;
          if (address?.geofence?.movingGeofence) {
            return buildMovingSiteMarker({
              geofence: address.geofence,
              id: commonProps.id,
              lat: commonProps.lat,
              lng: commonProps.lng,
            });
          }

          if (relevantGeofence && relevantGeofence.geometry.type === 'Polygon') {
            return {
              ...commonProps,
              geofenceType: GeofenceType.polygon,
              coordinates: relevantGeofence.geometry.coordinates,
            };
          } else {
            return {
              ...commonProps,
              geofenceType: null,
            };
          }
        }) as DefaultSiteMarker[] as ComponentProps<typeof MapV2>['markers']
    );
  }, [
    defaultSiteGeoFence,
    isStagingWaypointEnabled,
    getValues('dropOffLatLng'),
    getValues('pickUpLatLng'),
    getValues('stagingLatLng'),
    getValues('dropOffAddress'),
    getValues('pickUpAddress'),
    getValues('stagingAddress'),
  ]);

  const pickUpSelectorRef = useRef<SiteSelectorWithDefaultSitesHandler>(null);
  const dropOffSelectorRef = useRef<SiteSelectorWithDefaultSitesHandler>(null);

  // Synchronous dedupe of in-flight default-site removals, keyed by
  // `${waypointType}:${siteId}`. One component-level guard covers BOTH entry
  // points (the inline callout's × and the dropdown's delete icon), making
  // double-clicks safe. The state Set mirrors the ref so pending rows can
  // render a spinner + disabled × (the ref alone wouldn't re-render).
  const pendingDefaultSiteRemovalsRef = useRef<Set<string>>(new Set());
  const [pendingDefaultSiteRemovalKeys, setPendingDefaultSiteRemovalKeys] = useState<
    Set<string>
  >(new Set());

  const transformAddressOption = useCallback(
    (
      addressOption: AddressOption,
      waypointType: DeleteV1CompaniesCompanyIdProjectsProjectIdDefaultSitesWaypointTypeSiteIdData['path']['waypoint-type'],
    ) => {
      if (
        addressOption.siteId &&
        addressOption.type === AddressTypeSection.DefaultSites &&
        addressOption.sources?.length
      ) {
        // `sources` carries the parent entity (Order / Project) ids that
        // actually contribute this default site, so we fire only the DELETEs
        // that will hit a real record — no more blind dual-fire 404s.
        const { sources } = addressOption;
        const removalKey = `${waypointType}:${addressOption.siteId}`;

        return {
          ...addressOption,
          isRemovalPending: pendingDefaultSiteRemovalKeys.has(removalKey),
          onDelete: async () => {
            if (pendingDefaultSiteRemovalsRef.current.has(removalKey)) {
              return;
            }

            pendingDefaultSiteRemovalsRef.current.add(removalKey);
            setPendingDefaultSiteRemovalKeys((previous) =>
              new Set(previous).add(removalKey),
            );

            const selectorRef =
              waypointType === 'pickup' ? pickUpSelectorRef : dropOffSelectorRef;

            try {
              const siteId = String(addressOption.siteId);
              const scopedRequests: {
                request: Promise<AxiosResponse<void, unknown>>;
                scope: 'order' | 'project';
              }[] = [];

              for (const source of sources) {
                if (source.type === 'Order') {
                  scopedRequests.push({
                    scope: 'order',
                    request:
                      deleteV1CompaniesCompanyIdOrdersOrderIdDefaultSitesWaypointTypeSiteId(
                        {
                          path: {
                            'company-id': formCompanyId,
                            'order-id': source.id,
                            'site-id': siteId,
                            'waypoint-type': waypointType,
                          },
                        },
                      ),
                  });
                } else if (source.type === 'Project') {
                  scopedRequests.push({
                    scope: 'project',
                    request:
                      deleteV1CompaniesCompanyIdProjectsProjectIdDefaultSitesWaypointTypeSiteId(
                        {
                          path: {
                            'company-id': formCompanyId,
                            'project-id': source.id,
                            'site-id': siteId,
                            'waypoint-type': waypointType,
                          },
                        },
                      ),
                  });
                }
              }

              const results = await Promise.allSettled(
                scopedRequests.map(({ request }) => request),
              );

              const failedScopes = new Set<'order' | 'project'>();
              results.forEach((result, index) => {
                if (result.status === 'fulfilled') {
                  return;
                }

                // A 404 means the record is already gone (e.g. a retry after a
                // partial failure) — treat it as an already-removed success.
                const alreadyRemoved =
                  isAxiosError(result.reason) && result.reason.response?.status === 404;

                if (!alreadyRemoved) {
                  failedScopes.add(scopedRequests[index].scope);
                }
              });

              if (failedScopes.size > 0) {
                let errorMessage = t(
                  'errors.issue_removing_site_from_project_default_sites_list',
                );

                if (failedScopes.has('project') && failedScopes.has('order')) {
                  errorMessage = t(
                    'errors.issue_removing_site_from_project_and_order_default_sites_list',
                  );
                } else if (failedScopes.has('order')) {
                  errorMessage = t(
                    'errors.issue_removing_site_from_order_default_sites_list',
                  );
                }

                toasterStore.push(alert(errorMessage, AlertTypes.error));

                // Do NOT prune via onDefaultSiteRemoved here — that converts the
                // whole suggestion to a non-default entry and would hide the
                // still-valid source. Resetting instead makes the next dropdown
                // open refetch server truth and rebuild correct single-source
                // onDelete closures from the invalidated query.
                selectorRef.current?.resetSuggestions();
                return;
              }

              selectorRef.current?.onDefaultSiteRemoved(addressOption);

              // A site can be BOTH a persisted project default and a still-staged
              // order default at once: in New Order under a project, adding to the
              // default list POSTs the project record immediately AND stages an
              // order record for after create. Deleting the persisted record must
              // therefore also drop that staged addition, or the site silently
              // reappears as an order default once the order is created. No-op
              // when the parent has nothing staged for this site.
              onTempDefaultSiteRemoved?.({ siteId, waypointType });

              const removedFromOrder = scopedRequests.some(
                ({ scope }) => scope === 'order',
              );
              const removedFromProject = scopedRequests.some(
                ({ scope }) => scope === 'project',
              );

              let message = '';

              if (removedFromProject && removedFromOrder) {
                message = t('approvals.site_removed_from_default_project_and_order_list');
              } else if (removedFromProject) {
                message = t('approvals.site_removed_from_default_project_list');
              } else if (removedFromOrder) {
                message = t('approvals.site_removed_from_default_order_list');
              }

              toasterStore.push(alert(message, AlertTypes.success));
            } finally {
              pendingDefaultSiteRemovalsRef.current.delete(removalKey);
              setPendingDefaultSiteRemovalKeys((previous) => {
                const next = new Set(previous);
                next.delete(removalKey);
                return next;
              });

              // Always resync the merged cache — on success the removed site
              // drops off the map markers and dropdown; on failure the UI is
              // restored to whatever the server still has.
              await queryClient.invalidateQueries({ queryKey: ['defaultSites'] });
            }
          },
        };
      }

      return addressOption;
    },
    [
      formCompanyId,
      onTempDefaultSiteRemoved,
      pendingDefaultSiteRemovalKeys,
      queryClient,
      toasterStore,
    ],
  );

  // Default-site map markers are derived directly from the merged query — no
  // child→parent callback/effect, so there's no setState feedback path to loop on.
  const { defaultSites } = useDefaultSitesQuery({ companyId, orderId, projectId });

  const constructDefaultSiteMarkers = useCallback(
    (
      addressOptions: AddressOption[],
      markerType: MarkerType.pickup_site | MarkerType.dropoff_site,
    ) =>
      addressOptions
        .map((addressOption, ind) => {
          const { lat, lng } = addressOption;
          const latLngStr = `${lat}, ${lng} ind-${ind}`;

          if (addressOption.geofence?.movingGeofence) {
            return buildMovingSiteMarker({
              geofence: addressOption.geofence,
              id: latLngStr,
              lat: Number(lat),
              lng: Number(lng),
            });
          }

          let fence: ReturnType<typeof polygon> | ReturnType<typeof circle> | null = null;

          if (addressOption.geofence?.geojson) {
            fence = polygon(
              addressOption.geofence.geojson.coordinates,
              {},
              { id: addressOption.geofence.id },
            );
          } else if (
            addressOption.geofence?.circleCenter &&
            addressOption.geofence?.circleRadius
          ) {
            const center = [
              addressOption.geofence.circleCenter.lon,
              addressOption.geofence.circleCenter.lat,
            ];

            fence = circle(center, addressOption.geofence.circleRadius, {
              units: 'meters',
            });
          }

          const marker: DefaultSiteMarker = {
            id: latLngStr,
            lat: Number(lat),
            lng: Number(lng),
            type: markerType,
            isDefaultSite: true,
            ...(fence
              ? {
                  geofenceType: GeofenceType.polygon,
                  coordinates: fence.geometry.coordinates as [number, number][][],
                }
              : { geofenceType: null }),
          };

          return marker;
        })
        // Skip default sites whose coordinates parse to NaN (Sentry HORIZON-FE-2XP).
        .filter((marker) => Number.isFinite(marker.lat) && Number.isFinite(marker.lng)),
    [],
  );

  const defaultSitesMarkers = useMemo(
    () => ({
      pickup: constructDefaultSiteMarkers(defaultSites.pickup, MarkerType.pickup_site),
      dropoff: constructDefaultSiteMarkers(
        defaultSites.drop_off,
        MarkerType.dropoff_site,
      ),
    }),
    [constructDefaultSiteMarkers, defaultSites],
  );

  const consolidatedMarkersList = useMemo(() => {
    const defaultSitesMarkersList = [
      ...defaultSitesMarkers.pickup,
      ...defaultSitesMarkers.dropoff,
    ];

    return onlyShowDefaultSiteMarkers && defaultSitesMarkersList.length > 0
      ? // We can still allow markers to be selected without adding them to the default sites list
        [...defaultSitesMarkersList, ...(markersV2 ?? [])]
      : markersV2;
  }, [
    onlyShowDefaultSiteMarkers,
    defaultSitesMarkers.pickup,
    defaultSitesMarkers.dropoff,
    markersV2,
  ]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
        <SiteSelectorWithDefaultSites
          ref={pickUpSelectorRef}
          projectId={projectId}
          orderId={orderId}
          hideAddDefaultSiteAction={hideAddDefaultSiteAction}
          errors={errors}
          prefilledValue={prefilledPickUpValue}
          {...pickUpSelectorProps}
          companyId={companyId}
          transformAddressOption={
            allowRemoveDefaultSites
              ? (addressOption) => transformAddressOption(addressOption, 'pickup')
              : undefined
          }
          onlyDefaultSites={onlyDefaultSites}
          disabled={disableSiteSelection}
        />
        <SiteSelectorWithDefaultSites
          ref={dropOffSelectorRef}
          projectId={projectId}
          orderId={orderId}
          hideAddDefaultSiteAction={hideAddDefaultSiteAction}
          errors={errors}
          prefilledValue={prefilledDropOffValue}
          {...dropOffSelectorProps}
          companyId={companyId}
          transformAddressOption={
            allowRemoveDefaultSites
              ? (addressOption) => transformAddressOption(addressOption, 'drop_off')
              : undefined
          }
          onlyDefaultSites={onlyDefaultSites}
          disabled={disableSiteSelection}
        />
        {isStagingWaypointEnabled && (
          <SiteSelectorWithDefaultSites
            errors={errors}
            prefilledValue={prefilledStagingValue}
            {...stagingSelectorProps}
            companyId={companyId}
            hideAddDefaultSiteAction
            disabled={disableSiteSelection}
          />
        )}
      </Box>

      {showMap && (
        <Box sx={{ height: '300px' }}>
          <MapV2
            center={mapCenter}
            emptyText={t('navigation.choose_address')}
            markers={consolidatedMarkersList as TreadMarkerProps[]}
          />
        </Box>
      )}

      <Dialog
        isOpen={isDialogOpen}
        maxWidth="xl"
        onClose={handleCloseDialog}
        preventCloseOnBackdropClick
      >
        <DialogHeader onClose={handleCloseDialog}>
          {selectedSiteToEdit?.site
            ? t('administration.site.update_site')
            : t('administration.site.add_new')}
        </DialogHeader>

        <DialogContent>
          <SiteFormV2
            ref={siteFormRef}
            addressOption={selectedAddressOption?.addressOption}
            company={company}
            defaultSite={selectedSiteToEdit?.site}
            errors={asyncErrors}
            onFormStateChange={onSiteFormStateChange}
          />
        </DialogContent>

        <DialogActions>
          <SmallButton
            color="secondary"
            disabled={isDialogBusy}
            onClick={handleCloseDialog}
            variant="outlined"
          >
            {t('actions.cancel')}
          </SmallButton>

          <SmallLoadingButton
            loading={isDialogBusy}
            onClick={selectedSiteToEdit?.site ? onEditOrderSiteForm : onCreateNewSiteForm}
          >
            {t('actions.submit')}
          </SmallLoadingButton>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
